import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import SiteHeader from '../components/SiteHeader';
import { formatAgeFromDob } from '../utils/calculateAgeFromDob';
import {
  hasQuizResults,
  parseExtraDetails,
  type ExtraDetailsData,
  type PersonalityQuizResult,
  type QuizShareImages,
  type SingleQuizResult,
} from '../utils/extraDetails';

function renderQuizShareImages(shareImages?: QuizShareImages) {
  if (!shareImages) return null;
  const entries = Object.entries(shareImages).filter((entry): entry is [string, string] => Boolean(entry[1]));
  if (!entries.length) return null;

  return (
    <div style={{ marginTop: '10px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
      {entries.map(([aspect, url]) => (
        <a
          key={aspect}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}
        >
          <img
            src={url}
            alt={`Card ${aspect}`}
            style={{
              display: 'block',
              maxHeight: '140px',
              borderRadius: '6px',
              border: '1px solid var(--border-color)',
            }}
          />
          <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
            {aspect === 'story' ? '9:16 Story' : '3:4 Feed'}
          </span>
        </a>
      ))}
    </div>
  );
}

interface QuizShareStats {
  total: number;
  personality_count: number;
  single_count: number;
  promo_count: number;
  unique_sharers: number;
}

interface AdminUser {
  record_id: string;
  full_name?: string;
  email?: string;
  pin?: string;
  dob?: string;
  avatar?: string;
  height?: number | string;
  weight?: number | string;
  body_type?: string;
  city?: string;
  job?: string;
  income?: string;
  orientation?: string;
  role?: string;
  rel?: string;
  id_card?: string;
  nickname?: string;
  address?: string;
  education?: string;
  current_status?: string;
  living_space?: string;
  coming_out?: string;
  energy?: string;
  lifestyle?: string;
  hobbies?: string;
  pros?: string;
  cons?: string;
  substances?: string;
  age?: string;
  partner_role?: string;
  partner_body?: string;
  redflag?: string;
  message_to_admin?: string;
  extra_details?: string;
  [key: string]: string | number | null | undefined;
}

export default function AdminPage() {
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => !!localStorage.getItem('nagiAdminToken')
  );
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [activeTab, setActiveTab] = useState<'view' | 'edit' | 'samples'>('view');
  const [quizStats, setQuizStats] = useState<QuizShareStats | null>(null);
  const [samples, setSamples] = useState<any[]>([]);
  const [samplesLoading, setSamplesLoading] = useState(false);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('nagiAdminToken');
    setIsAuthenticated(false);
    setUsers([]);
  }, []);

  const fetchSamples = useCallback(async (token: string) => {
    setSamplesLoading(true);
    try {
      const res = await fetch('/api/admin/samples', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        handleLogout();
        return;
      }
      const data = await res.json();
      if (data.success) {
        setSamples(data.users || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSamplesLoading(false);
    }
  }, [handleLogout]);

  const handleSeedSamples = async () => {
    const token = localStorage.getItem('nagiAdminToken');
    if (!token) return;
    if (!confirm('Tạo 100 user mẫu với tên Việt Nam phổ biến (1-3 chữ)?')) return;
    try {
      const res = await fetch('/api/admin/samples', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action: 'seed' }),
      });
      const data = await res.json();
      alert(data.message || 'Seeded');
      fetchSamples(token);
    } catch (e) {
      alert('Seed failed');
    }
  };

  const handleExportSamples = () => {
    if (samples.length === 0) {
      alert('Không có mẫu để export');
      return;
    }
    const exportData = samples.map((s: any) => {
      let ed: any = {};
      try { ed = JSON.parse(s.extra_details || '{}'); } catch {}
      const pRes = ed.personalityResult || {};
      const sRes = ed.singleResult || {};
      const bRes = ed.bedroomResult || {};

      // Avatar: chỉ 1 link (string), không JSON
      let avatarLink = '';
      try {
        const av = JSON.parse(s.avatar || '[]');
        avatarLink = Array.isArray(av) ? (av[0] || '') : (s.avatar || '');
      } catch {
        avatarLink = s.avatar || '';
      }

      // Role: dạng JSON string cho dễ đọc/sửa
      let roleStr = '';
      try { roleStr = JSON.stringify(JSON.parse(s.role || '[]')); } catch { roleStr = s.role || ''; }

      return {
        record_id: s.record_id,
        nickname: s.nickname,
        dob: s.dob,
        city: s.city || '',
        height: s.height || '',
        weight: s.weight || '',
        body_type: s.body_type || '',
        job: s.job || '',
        income: s.income || '',
        education: s.education || '',
        current_status: s.current_status || '',
        living_space: s.living_space || '',
        orientation: s.orientation || '',
        coming_out: s.coming_out || '',
        rel: s.rel || '',
        role: roleStr,
        avatar: avatarLink,
        personality_variant: pRes.variantKey || '',
        single_variant: sRes.variantKey || '',
        bedroom_variant: bRes.variantKey || '',
      };
    });
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Samples');
    XLSX.writeFile(wb, 'samples_quiz_results.xlsx');
  };

  const handleImportSamples = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const token = localStorage.getItem('nagiAdminToken');
    if (!token) return;
    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws) as any[];
      const updates = rows.map((r: any) => {
        const update: any = { record_id: r.record_id };

        // Profile fields (avatar chỉ 1 link string)
        if (r.dob !== undefined) update.dob = r.dob;
        if (r.city !== undefined) update.city = r.city;
        if (r.height !== undefined) update.height = r.height;
        if (r.weight !== undefined) update.weight = r.weight;
        if (r.body_type !== undefined) update.body_type = r.body_type;
        if (r.job !== undefined) update.job = r.job;
        if (r.income !== undefined) update.income = r.income;
        if (r.education !== undefined) update.education = r.education;
        if (r.current_status !== undefined) update.current_status = r.current_status;
        if (r.living_space !== undefined) update.living_space = r.living_space;
        if (r.orientation !== undefined) update.orientation = r.orientation;
        if (r.coming_out !== undefined) update.coming_out = r.coming_out;
        if (r.rel !== undefined) update.rel = r.rel;

        if (r.role !== undefined) {
          try { update.role = JSON.parse(r.role); } catch { update.role = r.role; }
        }
        if (r.avatar !== undefined) {
          const av = r.avatar;
          if (typeof av === 'string' && av.trim() && !av.trim().startsWith('[')) {
            update.avatar = [av.trim()];  // 1 link → array 1 phần tử để tương thích
          } else {
            try {
              const parsed = typeof av === 'string' ? JSON.parse(av) : av;
              update.avatar = Array.isArray(parsed) ? parsed : (av ? [av] : []);
            } catch {
              update.avatar = av ? [av] : [];
            }
          }
        }

        // Quiz results: chỉ cần variant (A1, B2, C3...) là đủ, không cần JSON
        if (r.personality_variant) update.personality_variant = r.personality_variant;
        if (r.single_variant) update.single_variant = r.single_variant;
        if (r.bedroom_variant) update.bedroom_variant = r.bedroom_variant;

        return update;
      }).filter((u: any) => u.record_id);
      const res = await fetch('/api/admin/samples', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action: 'bulk_update', updates }),
      });
      const d = await res.json();
      alert(d.success ? 'Import thành công' : 'Lỗi');
      fetchSamples(token);
    } catch (err) {
      alert('Import lỗi: ' + err);
    }
    e.target.value = '';
  };

  const fetchQuizShares = useCallback(async (token: string) => {
    try {
      const res = await fetch(`/api/admin/quiz_shares?limit=1`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        handleLogout();
        return;
      }
      const data = (await res.json()) as {
        success?: boolean;
        stats?: QuizShareStats;
      };
      if (data.success) {
        setQuizStats(data.stats || null);
      }
    } catch (loadErr) {
      console.error(loadErr);
    }
  }, [handleLogout]);

  const fetchUsers = useCallback(async (token: string) => {
    setLoading(true);
    try {
      // Chỉ lấy user thật (loại bỏ hồ sơ mẫu is_sample=1)
      const res = await fetch('/api/admin/users?is_sample=0', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        handleLogout();
        return;
      }
      const data = (await res.json()) as { success?: boolean; users?: AdminUser[] };
      if (data.success && data.users) {
        setUsers(data.users);
      }
    } catch (loadErr) {
      console.error(loadErr);
    } finally {
      setLoading(false);
    }
  }, [handleLogout]);

  useEffect(() => {
    const token = localStorage.getItem('nagiAdminToken');
    if (!token) return;

    let cancelled = false;
    const loadUsers = async () => {
      setLoading(true);
      try {
        const res = await fetch('/api/admin/users?is_sample=0', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (res.status === 401) {
          handleLogout();
          return;
        }
        const data = (await res.json()) as { success?: boolean; users?: AdminUser[] };
        if (!cancelled && data.success && data.users) {
          setUsers(data.users);
        }
        if (!cancelled) {
          void fetchQuizShares(token);
        }
      } catch (loadErr) {
        if (!cancelled) console.error(loadErr);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void loadUsers();
    void fetchSamples(token);
    return () => {
      cancelled = true;
    };
  }, [handleLogout, fetchQuizShares, fetchSamples]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const res = await fetch('/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      const data = await res.json();
      if (res.ok && data.token) {
        localStorage.setItem('nagiAdminToken', data.token);
        setIsAuthenticated(true);
        fetchUsers(data.token);
        fetchQuizShares(data.token);
      } else {
        setError(data.error || 'Sai mật khẩu');
      }
    } catch {
      setError('Lỗi kết nối');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncSheets = async () => {
    const token = localStorage.getItem('nagiAdminToken');
    if (!token) return;
    
    if (!window.confirm('Bạn có chắc chắn muốn đẩy toàn bộ dữ liệu này sang Google Sheets? Quá trình này có thể mất vài phút tuỳ thuộc vào số lượng hồ sơ.')) return;
    
    try {
      const res = await fetch('/api/admin/sync', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        alert(`Đã ra lệnh đồng bộ ${data.count} hồ sơ! Quá trình sẽ tiếp tục chạy ngầm trên máy chủ.`);
      } else {
        alert(data.error || 'Lỗi đồng bộ');
      }
    } catch (err) {
      console.error(err);
      alert('Lỗi kết nối mạng khi đồng bộ');
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Bạn có chắc chắn muốn xoá vĩnh viễn ${selectedIds.length} hồ sơ này không? Hành động này không thể hoàn tác.`)) return;

    const token = localStorage.getItem('nagiAdminToken');
    setLoading(true);
    try {
      const res = await fetch('/api/admin/delete_users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ids: selectedIds })
      });
      const data = await res.json();
      if (data.success) {
        alert(`Đã xoá thành công ${data.count} hồ sơ.`);
        setSelectedIds([]);
        fetchUsers(token!);
      } else {
        alert(data.error || 'Lỗi khi xoá');
      }
    } catch (err) {
      console.error(err);
      alert('Lỗi kết nối mạng');
    } finally {
      setLoading(false);
    }
  };

  const getFirstAvatar = (avatarStr: string | undefined) => {
    if (!avatarStr) return null;
    try {
      const arr = typeof avatarStr === 'string' ? JSON.parse(avatarStr) : avatarStr;
      if (Array.isArray(arr) && arr.length > 0) return arr[0];
      if (typeof arr === 'string' && arr.startsWith('http')) return arr;
    } catch {
      // maybe it's just a raw url
      if (avatarStr.startsWith('http')) return avatarStr;
    }
    return null;
  };

  const getAllImages = (avatarStr: string | undefined) => {
    if (!avatarStr) return [];
    try {
      const arr = typeof avatarStr === 'string' ? JSON.parse(avatarStr) : avatarStr;
      if (Array.isArray(arr)) return arr.filter(url => typeof url === 'string' && url.startsWith('http'));
      if (typeof arr === 'string' && arr.startsWith('http')) return [arr];
    } catch {
      if (avatarStr.startsWith('http')) return [avatarStr];
    }
    return [];
  };

  const parseArray = (str: string | undefined) => {
    if (!str) return [];
    try {
      const arr = typeof str === 'string' ? JSON.parse(str) : str;
      return Array.isArray(arr) ? arr : [str];
    } catch {
      return [str];
    }
  };

  const formatRoleOrRel = (value: string | undefined): string => {
    if (!value) return '---';
    if (value.startsWith('[')) {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed.join(', ') : value;
      } catch {
        return value;
      }
    }
    return value;
  };

  const renderTags = (str: string | undefined, color: string) => {
    const arr = parseArray(str);
    if (!arr || arr.length === 0 || (arr.length === 1 && !arr[0])) return <span style={{ color: '#888' }}>---</span>;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        {arr.map((item: string, i: number) => (
          <span key={i} style={{ background: color, color: '#fff', padding: '4px 10px', borderRadius: '12px', fontSize: '0.85rem' }}>
            {item}
          </span>
        ))}
      </div>
    );
  };

  if (!isAuthenticated) {
    return (
      <div className="app-container">
        <SiteHeader />
        <div className="step-container" style={{ maxWidth: '400px', width: '100%', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center' }}>Đăng nhập Quản trị</h2>
          <form onSubmit={handleLogin}>
            <div className="form-group" style={{ marginTop: '20px' }}>
              <input 
                type="password" 
                placeholder="Nhập mật khẩu..." 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                required 
              />
            </div>
            {error && <p style={{ color: 'var(--error-color)', marginBottom: '15px' }}>{error}</p>}
            <button type="submit" className="primary-btn" style={{ width: '100%' }} disabled={loading}>
              {loading ? 'Đang kiểm tra...' : 'Vào trang Admin'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  const filteredUsers = users.filter(u => 
    (u.full_name || '').toLowerCase().includes(search.toLowerCase()) || 
    (u.email || '').toLowerCase().includes(search.toLowerCase()) ||
    (u.pin || '').includes(search)
  );

  return (
    <div className="admin-page">
      <SiteHeader className="site-header--admin">
        <h2 className="admin-page__title">Dashboard Quản trị viên</h2>
      </SiteHeader>
      <div className="admin-toolbar">
        <button onClick={handleSyncSheets} style={{ background: '#10b981', color: 'white', border: 'none', padding: '10px 15px', borderRadius: '8px', cursor: 'pointer' }}>
          Đồng bộ Google Sheets
        </button>
        <button onClick={handleLogout} style={{ background: 'var(--error-color)', color: 'white', border: 'none', padding: '10px 15px', borderRadius: '8px', cursor: 'pointer' }}>
          Đăng xuất
        </button>
      </div>

      <div style={{ background: 'var(--surface-color)', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.2)', border: '1px solid var(--border-color)', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h3 style={{ margin: 0 }}>Thống kê thẻ Quiz đã chia sẻ</h3>
            <p style={{ margin: '6px 0 0', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              Chỉ hiển thị số lượng (đếm lượt làm quiz). Phần xem trước hình ảnh đã bỏ để tối ưu admin.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              const token = localStorage.getItem('nagiAdminToken');
              if (token) fetchQuizShares(token);
            }}
            style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'transparent', cursor: 'pointer', fontSize: '0.85rem' }}
          >
            ↻ Làm mới thống kê
          </button>
        </div>

        {quizStats && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '20px' }}>
            <div style={{ background: 'rgba(255,255,255,0.04)', padding: '12px', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Tổng ảnh</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{quizStats.total}</div>
            </div>
            <div style={{ background: 'rgba(59,130,246,0.08)', padding: '12px', borderRadius: '8px', border: '1px solid rgba(59,130,246,0.2)' }}>
              <div style={{ fontSize: '0.8rem', color: '#93c5fd' }}>Gu khi yêu</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{quizStats.personality_count}</div>
            </div>
            <div style={{ background: 'rgba(125,211,252,0.08)', padding: '12px', borderRadius: '8px', border: '1px solid rgba(125,211,252,0.2)' }}>
              <div style={{ fontSize: '0.8rem', color: '#7dd3fc' }}>Trái tim băng giá</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{quizStats.single_count}</div>
            </div>
            <div style={{ background: 'rgba(16,185,129,0.08)', padding: '12px', borderRadius: '8px', border: '1px solid rgba(16,185,129,0.2)' }}>
              <div style={{ fontSize: '0.8rem', color: '#6ee7b7' }}>Dùng quảng bá</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{quizStats.promo_count}</div>
            </div>
          </div>
        )}

        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '8px' }}>
          Chỉ hiển thị số lượng thẻ card đã lưu (dùng cho thống kê và quảng bá). Phần xem trước hình ảnh đã bỏ để tối ưu trang admin.
        </p>
      </div>

      {/* Samples Management */}
      <div style={{ background: 'var(--surface-color)', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.2)', border: '1px solid var(--border-color)', marginTop: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <div>
            <h3 style={{ margin: 0 }}>Hồ sơ Mẫu (is_sample)</h3>
            <div style={{ fontSize: '0.75rem', color: '#888', marginTop: 2 }}>
              record_id được tạo bằng CHÍNH XÁC cùng hàm với người thật (generateRecordId). Link /u/xxxxxxx y hệt, không phân biệt được sample hay thật.
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={() => fetchSamples(localStorage.getItem('nagiAdminToken') || '')} className="quiz-btn quiz-btn--outline" style={{ padding: '8px 16px' }}>
              Tải lại
            </button>
            <button onClick={handleSeedSamples} className="quiz-btn quiz-btn--primary-gold" style={{ padding: '8px 16px' }}>
              Tạo lại 100 User Mẫu (record_id tự nhiên)
            </button>
            <button 
              onClick={async () => {
                const token = localStorage.getItem('nagiAdminToken');
                if (!token) return;
                if (!confirm('Cập nhật phân bổ (city, role, body type, chiều cao/cân nặng, nghề nghiệp...) cho 100 user mẫu hiện tại? (giữ nguyên record_id, tên và quiz results)')) return;
                try {
                  const res = await fetch('/api/admin/samples', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ action: 'refresh_distributions' }),
                  });
                  const d = await res.json();
                  alert(d.message || 'Đã cập nhật');
                  fetchSamples(token);
                } catch (e) {
                  alert('Lỗi: ' + e);
                }
              }} 
              className="quiz-btn quiz-btn--outline" 
              style={{ padding: '8px 16px' }}
            >
              Cập nhật phân bổ (giữ nguyên tên & record_id)
            </button>
            <button onClick={handleExportSamples} className="quiz-btn quiz-btn--outline" style={{ padding: '8px 16px' }}>
              Export Excel (Profile + Quiz variants + full results)
            </button>
            <label className="quiz-btn quiz-btn--outline" style={{ padding: '8px 16px', cursor: 'pointer' }}>
              Import Excel
              <input type="file" accept=".xlsx" onChange={handleImportSamples} style={{ display: 'none' }} />
            </label>
          </div>
        </div>

        {samplesLoading ? (
          <p>Đang tải...</p>
        ) : (
          <div style={{ maxHeight: '400px', overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                  <th style={{ padding: '8px', textAlign: 'left' }}>Nickname + Public Link</th>
                  <th style={{ padding: '8px', textAlign: 'left' }}>Email (để login mẫu)</th>
                  <th style={{ padding: '8px', textAlign: 'left' }}>DOB + TP</th>
                  <th style={{ padding: '8px', textAlign: 'left' }}>Job · Income / Role / KQ</th>
                </tr>
              </thead>
              <tbody>
                {samples.map((s: any, idx: number) => {
                  let roles = '';
                  try { roles = (JSON.parse(s.role || '[]') || []).join(', '); } catch {}
                  let hasResults = '—';
                  try {
                    const ed = JSON.parse(s.extra_details || '{}');
                    const hasP = !!ed.personalityResult?.shareTitle;
                    const hasS = !!ed.singleResult?.shareTitle;
                    const hasB = !!ed.bedroomResult?.shareTitle;
                    hasResults = [hasP && 'Gu', hasS && 'Tim', hasB && 'Phòng'].filter(Boolean).join('+') || '—';
                  } catch {}
                  const publicLink = `/u/${s.record_id}`;
                  return (
                    <tr key={idx} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '8px' }}>
                        <div style={{ fontWeight: 600 }}>{s.nickname}</div>
                        <a href={publicLink} target="_blank" rel="noreferrer" style={{ fontSize: '0.65rem', color: '#60a5fa' }}>
                          {s.record_id} ↗
                        </a>
                      </td>
                      <td style={{ padding: '8px' }}>{s.email}</td>
                      <td style={{ padding: '8px' }}>{s.dob}<br /><span style={{ fontSize: '0.65rem', color: '#888' }}>{s.city}</span></td>
                      <td style={{ padding: '8px', fontSize: '0.75rem' }}>
                        {s.job || '---'} · {s.income || '---'}<br />
                        {roles} <br /> KQ: {hasResults}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ background: 'var(--surface-color)', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.2)', border: '1px solid var(--border-color)' }}>
        <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ display: 'inline-block', marginRight: '20px' }}>Danh sách Hồ sơ Thật ({users.length}) <span style={{ fontSize: '0.7rem', color: '#888' }}>(không gồm mẫu is_sample)</span></h3>
            {selectedIds.length > 0 && (
              <button 
                onClick={handleDeleteSelected}
                style={{ background: 'var(--error-color)', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem' }}>
                🗑 Xoá {selectedIds.length} mục
              </button>
            )}
          </div>
          <input 
            type="text" 
            placeholder="Tìm theo Tên, SĐT..." 
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: '300px', marginBottom: 0 }}
          />
        </div>

        {loading && users.length === 0 ? (
          <p>Đang tải dữ liệu...</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border-color)', color: 'var(--text-secondary)' }}>
                  <th style={{ padding: '12px 8px', width: '40px' }}>
                    <input 
                      type="checkbox" 
                      checked={selectedIds.length === filteredUsers.length && filteredUsers.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedIds(filteredUsers.map(u => u.record_id));
                        } else {
                          setSelectedIds([]);
                        }
                      }}
                    />
                  </th>
                  <th style={{ padding: '12px 8px' }}>Hồ sơ (Bấm tên để sửa)</th>
                  <th style={{ padding: '12px 8px' }}>Liên hệ</th>
                  <th style={{ padding: '12px 8px' }}>Ngoại hình</th>
                  <th style={{ padding: '12px 8px' }}>Nền tảng</th>
                  <th style={{ padding: '12px 8px' }}>Bản sắc & Mục tiêu</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => {
                  const avatarUrl = getFirstAvatar(user.avatar);
                  const isChecked = selectedIds.includes(user.record_id);
                  const quizMeta = parseExtraDetails(user.extra_details);
                  return (
                  <tr key={user.record_id} style={{ borderBottom: '1px solid var(--border-color)', background: isChecked ? 'rgba(212, 175, 55, 0.1)' : 'transparent' }}>
                    <td style={{ padding: '12px 8px' }}>
                      <input 
                        type="checkbox" 
                        checked={isChecked}
                        onChange={(e) => {
                          if (e.target.checked) setSelectedIds([...selectedIds, user.record_id]);
                          else setSelectedIds(selectedIds.filter(id => id !== user.record_id));
                        }}
                      />
                    </td>
                    <td style={{ padding: '12px 8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {avatarUrl ? (
                          <img src={avatarUrl} alt="avatar" style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }} />
                        ) : (
                          <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#333', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}>
                            👤
                          </div>
                        )}
                        <div>
                          <div 
                            onClick={() => { setEditingUser(user); setActiveTab('view'); }}
                            style={{ fontWeight: 'bold', color: 'var(--accent-color)', cursor: 'pointer', textDecoration: 'underline' }}>
                            {user.full_name || 'Chưa cập nhật'}
                          </div>
                          <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                            {formatAgeFromDob(user.dob)}
                          </div>
                          {hasQuizResults(quizMeta) && (
                            <div style={{ fontSize: '0.72rem', marginTop: '4px', display: 'flex', gap: '6px' }}>
                              {quizMeta?.personalityResult && <span title="Gu khi yêu">🎭 Quiz</span>}
                              {quizMeta?.singleResult && <span title="Trái tim băng giá">🧊 Quiz</span>}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '12px 8px', fontSize: '0.9rem' }}>
                      <div>{user.email || '---'}</div>
                      {user.pin && <div style={{ fontSize: '0.8rem', color: '#888' }}>PIN: {user.pin}</div>}
                    </td>
                    <td style={{ padding: '12px 8px', fontSize: '0.9rem' }}>
                      <div>{user.height ? `${user.height}cm` : '---'} {user.weight ? `- ${user.weight}kg` : ''}</div>
                      <div style={{ fontSize: '0.8rem', color: '#888' }}>{user.body_type ? user.body_type.split('(')[0].trim() : '---'}</div>
                    </td>
                    <td style={{ padding: '12px 8px', fontSize: '0.9rem' }}>
                      <div>{user.city || '---'}</div>
                      <div style={{ fontSize: '0.8rem', color: '#888' }}>{user.job || '---'}</div>
                      <div style={{ fontSize: '0.8rem', color: '#888' }}>{user.income || '---'}</div>
                    </td>
                    <td style={{ padding: '12px 8px', fontSize: '0.9rem' }}>
                      <div style={{ color: 'var(--accent-hover)' }}>{user.orientation || '---'} / {formatRoleOrRel(user.role)}</div>
                      <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '2px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {formatRoleOrRel(user.rel)}
                      </div>
                    </td>
                  </tr>
                )})}
                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '20px' }}>Không tìm thấy hồ sơ nào.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editingUser && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: 'var(--surface-color)', borderRadius: '12px', width: '100%', maxWidth: '800px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', border: '1px solid var(--border-color)', overflow: 'hidden' }}>
            {/* Header Tabs */}
            <div style={{ display: 'flex', borderBottom: '1px solid var(--border-color)' }}>
              <button 
                onClick={() => setActiveTab('view')}
                style={{ flex: 1, padding: '15px', background: activeTab === 'view' ? 'var(--accent-color)' : 'transparent', color: activeTab === 'view' ? 'var(--bg-color)' : 'var(--text-secondary)', border: 'none', fontWeight: 'bold', fontSize: '1rem', cursor: 'pointer', borderRadius: 0 }}>
                Hồ Sơ Trực Quan
              </button>
              <button 
                onClick={() => setActiveTab('edit')}
                style={{ flex: 1, padding: '15px', background: activeTab === 'edit' ? 'var(--accent-color)' : 'transparent', color: activeTab === 'edit' ? 'var(--bg-color)' : 'var(--text-secondary)', border: 'none', fontWeight: 'bold', fontSize: '1rem', cursor: 'pointer', borderRadius: 0, borderLeft: '1px solid var(--border-color)' }}>
                Sửa Dữ Liệu
              </button>
              <button onClick={() => setEditingUser(null)} style={{ background: 'transparent', border: 'none', color: 'var(--text-primary)', fontSize: '24px', padding: '0 20px', cursor: 'pointer', borderLeft: '1px solid var(--border-color)' }}>&times;</button>
            </div>

            {/* Content Area */}
            <div style={{ overflowY: 'auto', flex: 1, padding: '20px' }}>
              {activeTab === 'view' ? (
                <div>
                  {/* Gallery */}
                  <div style={{ display: 'flex', gap: '15px', overflowX: 'auto', paddingBottom: '15px', marginBottom: '20px', borderBottom: '1px solid var(--border-color)' }}>
                    {getAllImages(editingUser.avatar).map((url: string, i: number) => (
                      <img key={i} src={url} alt={`avatar-${i}`} style={{ height: '200px', borderRadius: '8px', objectFit: 'cover' }} />
                    ))}
                    {editingUser.id_card && (
                      <div style={{ position: 'relative' }}>
                        <div style={{ position: 'absolute', top: 5, left: 5, background: 'red', color: 'white', padding: '2px 6px', fontSize: '0.7rem', borderRadius: '4px', fontWeight: 'bold' }}>CCCD</div>
                        <img src={editingUser.id_card} alt="CCCD" style={{ height: '200px', borderRadius: '8px', objectFit: 'cover' }} />
                      </div>
                    )}
                  </div>

                  {/* Profile Cards */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    <div style={{ background: 'rgba(255,255,255,0.03)', padding: '15px', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                      <h4 style={{ color: 'var(--accent-color)', marginBottom: '10px' }}>📌 Thông Tin Hành Chính</h4>
                      <p><strong>Họ Tên:</strong> {editingUser.full_name} ({editingUser.nickname})</p>
                      <p>
                        <strong>Ngày Sinh:</strong> {editingUser.dob || '---'}
                        {editingUser.dob ? ` (${formatAgeFromDob(editingUser.dob)})` : ''}
                      </p>
                      <p><strong>Liên Hệ:</strong> {editingUser.email} / PIN: {editingUser.pin}</p>
                      <p><strong>Địa Chỉ:</strong> {editingUser.address}, {editingUser.city}</p>
                    </div>

                    <div style={{ background: 'rgba(255,255,255,0.03)', padding: '15px', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                      <h4 style={{ color: 'var(--accent-color)', marginBottom: '10px' }}>👤 Ngoại Hình & Nền Tảng</h4>
                      <p><strong>Chiều Cao/Cân Nặng:</strong> {editingUser.height}cm - {editingUser.weight}kg</p>
                      <p><strong>Tạng Người:</strong> {editingUser.body_type}</p>
                      <p><strong>Học Vấn:</strong> {editingUser.education}</p>
                      <p><strong>Nghề Nghiệp:</strong> {editingUser.job}</p>
                      <p><strong>Thu Nhập:</strong> {editingUser.income}</p>
                    </div>

                    <div style={{ background: 'rgba(255,255,255,0.03)', padding: '15px', borderRadius: '8px', border: '1px solid var(--border-color)', gridColumn: '1 / -1' }}>
                      <h4 style={{ color: 'var(--accent-color)', marginBottom: '10px' }}>🌈 Bản Sắc & Lối Sống</h4>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                        <div>
                          <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>Xu Hướng & Role:</p>
                          <p>{editingUser.orientation} / {parseArray(editingUser.role).join(', ')}</p>
                          <p><strong>Tình Trạng:</strong> {editingUser.current_status} - {editingUser.living_space}</p>
                          <p><strong>Come out:</strong> {editingUser.coming_out}</p>
                        </div>
                        <div>
                          <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>Năng Lượng & Lối Sống:</p>
                          <p>{editingUser.energy} / {editingUser.lifestyle}</p>
                        </div>
                      </div>
                      
                      <div style={{ marginTop: '15px' }}>
                        <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>Sở Thích:</p>
                        {renderTags(editingUser.hobbies, '#3b82f6')}
                      </div>
                      <div style={{ marginTop: '15px', display: 'flex', gap: '20px' }}>
                        <div style={{ flex: 1 }}>
                          <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>Ưu Điểm:</p>
                          {renderTags(editingUser.pros, '#10b981')}
                        </div>
                        <div style={{ flex: 1 }}>
                          <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>Nhược Điểm:</p>
                          {renderTags(editingUser.cons, '#f59e0b')}
                        </div>
                      </div>
                      <div style={{ marginTop: '15px' }}>
                        <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>Chất Kích Thích:</p>
                        {renderTags(editingUser.substances, '#6b7280')}
                      </div>
                    </div>

                    <div style={{ background: 'rgba(255,255,255,0.03)', padding: '15px', borderRadius: '8px', border: '1px solid var(--border-color)', gridColumn: '1 / -1' }}>
                      <h4 style={{ color: 'var(--accent-color)', marginBottom: '10px' }}>🎯 Mục Tiêu & Tiêu Chuẩn</h4>
                      <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>Mối quan hệ tìm kiếm:</p>
                      {renderTags(editingUser.rel, '#8b5cf6')}
                      
                      <div style={{ marginTop: '15px' }}>
                        <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>Gu Đối Phương (Age, Role, Body):</p>
                        {renderTags(editingUser.age, '#ec4899')}
                        <div style={{ marginTop: '5px' }}>{renderTags(editingUser.partner_role, '#ec4899')}</div>
                        <div style={{ marginTop: '5px' }}>{renderTags(editingUser.partner_body, '#ec4899')}</div>
                      </div>

                      <div style={{ marginTop: '15px' }}>
                        <p style={{ fontWeight: 'bold', marginBottom: '5px', color: 'var(--error-color)' }}>Redflag (Không chấp nhận):</p>
                        {renderTags(editingUser.redflag, 'var(--error-color)')}
                      </div>

                      {editingUser.message_to_admin && (
                        <div style={{ marginTop: '15px', padding: '10px', background: 'rgba(212,175,55,0.1)', borderLeft: '4px solid var(--accent-color)' }}>
                          <p style={{ fontWeight: 'bold', marginBottom: '5px' }}>Lời nhắn cho Admin:</p>
                          <p><i>"{editingUser.message_to_admin}"</i></p>
                        </div>
                      )}
                    </div>

                    {/* Card 7: Thông tin bổ sung & Trắc nghiệm */}
                    {(() => {
                      const ext = parseExtraDetails(editingUser.extra_details) as ExtraDetailsData | null;
                      if (!ext) return null;
                      
                      const hasAnswers = ext.additionalAnswers && Object.keys(ext.additionalAnswers).length > 0;
                      const hasPersonality = !!ext.personalityResult;
                      const hasSingle = !!ext.singleResult;
                      
                      if (!hasAnswers && !hasPersonality && !hasSingle) return null;

                      return (
                        <div style={{ background: 'rgba(255,255,255,0.03)', padding: '15px', borderRadius: '8px', border: '1px solid var(--border-color)', gridColumn: '1 / -1' }}>
                          <h4 style={{ color: 'var(--accent-color)', marginBottom: '15px' }}>🧩 Thông Tin Bổ Sung & Kết Quả Trắc Nghiệm</h4>
                          
                          {hasAnswers && (
                            <div style={{ marginBottom: '20px' }}>
                              <p style={{ fontWeight: 'bold', color: 'var(--accent-color)', fontSize: '0.9rem', marginBottom: '8px' }}>💬 Câu Hỏi Tìm Hiểu Sâu:</p>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: '10px' }}>
                                {Object.entries(ext.additionalAnswers ?? {}).map(([q, a]) => (
                                  a ? (
                                    <div key={q} style={{ fontSize: '0.85rem' }}>
                                      <p style={{ fontWeight: '600', margin: '0 0 4px 0' }}>• {q}</p>
                                      <p style={{ margin: 0, color: 'var(--text-secondary)', paddingLeft: '10px', fontStyle: 'italic' }}>"{a}"</p>
                                    </div>
                                  ) : null
                                ))}
                              </div>
                            </div>
                          )}

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                            {hasPersonality && (() => {
                              const r = ext.personalityResult as PersonalityQuizResult;
                              return (
                                <div style={{ background: 'rgba(59,130,246,0.05)', padding: '10px', borderRadius: '6px', border: '1px solid rgba(59,130,246,0.1)' }}>
                                  <p style={{ fontWeight: 'bold', color: '#3b82f6', fontSize: '0.85rem', margin: '0 0 5px 0' }}>🎭 Gu khi yêu</p>
                                  <p style={{ fontWeight: 'bold', fontSize: '0.95rem', margin: '0 0 4px 0' }}>{r.title}</p>
                                  {r.shareTitle && (
                                    <p style={{ fontSize: '0.8rem', color: '#7dd3fc', margin: '0 0 4px 0' }}>{r.shareTitle}</p>
                                  )}
                                  <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 6px 0' }}>
                                    Khớp {r.percent}% · nhóm {r.typeKey}
                                    {r.variantKey ? ` · biến thể ${r.variantKey}` : ''}
                                  </p>
                                  {r.shareHook && (
                                    <p style={{ fontSize: '0.78rem', color: 'var(--text-primary)', margin: '0 0 6px 0', fontStyle: 'italic' }}>
                                      “{r.shareHook}”
                                    </p>
                                  )}
                                  <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: 0, whiteSpace: 'pre-line' }}>{r.desc}</p>
                                  {renderQuizShareImages(r.shareImages)}
                                </div>
                              );
                            })()}

                            {hasSingle && (() => {
                              const r = ext.singleResult as SingleQuizResult;
                              return (
                                <div style={{ background: 'rgba(125,211,252,0.05)', padding: '10px', borderRadius: '6px', border: '1px solid rgba(125,211,252,0.15)' }}>
                                  <p style={{ fontWeight: 'bold', color: '#7dd3fc', fontSize: '0.85rem', margin: '0 0 5px 0' }}>🧊 Trái tim băng giá</p>
                                  <p style={{ fontWeight: 'bold', fontSize: '0.95rem', margin: '0 0 4px 0' }}>{r.title}</p>
                                  {r.shareTitle && (
                                    <p style={{ fontSize: '0.8rem', color: '#7dd3fc', margin: '0 0 4px 0' }}>{r.shareTitle}</p>
                                  )}
                                  <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 4px 0' }}>
                                    {r.temperature}
                                    {r.temperatureLabel ? ` (${r.temperatureLabel})` : ''}
                                    {' · '}Độ khớp {r.percent}%
                                    {r.variantKey ? ` · biến thể ${r.variantKey}` : ''}
                                  </p>
                                  {r.triggerTagD && (
                                    <p style={{ fontSize: '0.72rem', color: '#fb7185', fontWeight: 700, margin: '0 0 6px 0' }}>
                                      ⚠️ WARNING: LOWKEY LOVER
                                    </p>
                                  )}
                                  {r.shareHook && (
                                    <p style={{ fontSize: '0.78rem', color: 'var(--text-primary)', margin: '0 0 6px 0', fontStyle: 'italic' }}>
                                      “{r.shareHook}”
                                    </p>
                                  )}
                                  <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: 0, whiteSpace: 'pre-line' }}>{r.desc}</p>
                                  {renderQuizShareImages(r.shareImages)}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      );
                    })()}

                  </div>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  {Object.keys(editingUser).filter(k => k !== 'record_id').map(key => (
                    <div key={key}>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                        {key}
                      </label>
                      <input 
                        type="text" 
                        value={editingUser[key] || ''} 
                        onChange={(e) => setEditingUser({ ...editingUser, [key]: e.target.value })}
                        style={{ marginBottom: 0 }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {activeTab === 'edit' && (
              <div style={{ padding: '20px', borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button onClick={() => setEditingUser(null)} style={{ padding: '8px 16px', background: 'transparent', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '6px', cursor: 'pointer' }}>Hủy</button>
                <button 
                  onClick={async () => {
                    const token = localStorage.getItem('nagiAdminToken');
                    try {
                      const res = await fetch('/api/admin/update_user', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                        body: JSON.stringify(editingUser)
                      });
                      if (res.ok) {
                        alert('Lưu thành công!');
                        setEditingUser(null);
                        fetchUsers(token!);
                      } else {
                        alert('Lỗi khi lưu');
                      }
                    } catch {
                      alert('Lỗi kết nối mạng');
                    }
                  }}
                  style={{ padding: '8px 16px', background: 'var(--primary-color)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                >
                  Lưu Thay Đổi
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
